import { useMemo } from "react";

/**
 * Custom 12-hour AM/PM time picker.
 *
 * Stores/reads values as 24-hour "HH:MM" strings (matching MongoDB schema)
 * so existing data on disk doesn't have to change. Display is 12-hour with
 * a separate AM/PM toggle. No reliance on browser locale (which is why the
 * native <input type="time"> sometimes shows 24-hour on Android/Linux).
 *
 * Props:
 *   value: "HH:MM" string (24-hour)
 *   onChange: (newValue: "HH:MM") => void
 *   minuteStep: number (default 15) — granularity of the minutes dropdown
 *   className: string — wrapper classes
 *   testIdPrefix: string
 */
export default function TimePicker12({ value = "", onChange, minuteStep = 15, className = "", testIdPrefix = "time" }) {
  const [h24, m] = useMemo(() => {
    const [hh, mm] = (value || "").split(":").map(n => parseInt(n, 10));
    return [Number.isFinite(hh) ? hh : 8, Number.isFinite(mm) ? mm : 0];
  }, [value]);

  const period = h24 >= 12 ? "PM" : "AM";
  const h12 = ((h24 + 11) % 12) + 1;

  const hours = useMemo(() => Array.from({ length: 12 }, (_, i) => i + 1), []);
  const minutes = useMemo(
    () => Array.from({ length: Math.ceil(60 / minuteStep) }, (_, i) => i * minuteStep),
    [minuteStep],
  );

  const emit = (newH12, newMin, newPeriod) => {
    const hh = newPeriod === "AM"
      ? (newH12 === 12 ? 0 : newH12)
      : (newH12 === 12 ? 12 : newH12 + 12);
    onChange(`${String(hh).padStart(2, "0")}:${String(newMin).padStart(2, "0")}`);
  };

  const selectCls = "h-12 rounded-md border border-slate-200 bg-white px-2 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/40 focus:border-[#0F4C81]";

  return (
    <div className={`flex flex-wrap items-center gap-x-1.5 gap-y-1.5 ${className}`}>
      <select
        value={h12}
        onChange={(e) => emit(parseInt(e.target.value, 10), m, period)}
        className={`${selectCls} w-[64px]`}
        data-testid={`${testIdPrefix}-hour`}
      >
        {hours.map(h => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>
      <span className="text-slate-400 font-bold">:</span>
      <select
        value={m - (m % minuteStep)}
        onChange={(e) => emit(h12, parseInt(e.target.value, 10), period)}
        className={`${selectCls} w-[64px]`}
        data-testid={`${testIdPrefix}-minute`}
      >
        {minutes.map(mm => (
          <option key={mm} value={mm}>{String(mm).padStart(2, "0")}</option>
        ))}
      </select>
      <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5 text-xs font-bold">
        <button
          type="button"
          onClick={() => emit(h12, m, "AM")}
          className={`px-2.5 py-2 rounded ${period === "AM" ? "bg-[#0F4C81] text-white" : "text-slate-500 hover:text-slate-700"}`}
          data-testid={`${testIdPrefix}-am`}
        >AM</button>
        <button
          type="button"
          onClick={() => emit(h12, m, "PM")}
          className={`px-2.5 py-2 rounded ${period === "PM" ? "bg-[#0F4C81] text-white" : "text-slate-500 hover:text-slate-700"}`}
          data-testid={`${testIdPrefix}-pm`}
        >PM</button>
      </div>
    </div>
  );
}
